/**
 * The sidebar, written as permissions.
 *
 * One map, read by four callers, so a rule is stated once:
 *
 * - `app-shell.tsx`       — which sidebar rows a person sees
 * - `AppRouter.tsx`       — which addresses open, for the person who types one
 * - `ConfigurationPage`   — which sections of the Configuration rail are listed
 * - `role-editor.tsx`     — how the permission grid is grouped and labelled
 *
 * Hiding is not enforcing. Every codename here also guards the route that
 * serves the data, so a hidden row is a row that would have been refused; the
 * menu is the courtesy, the API is the rule.
 *
 * `perms` is read the way the backend reads `requires(..., require_all=False)`:
 * any one codename is enough, and an empty list means everyone. `models` names
 * every kind of record administered from the area — including the ones with no
 * page of their own — so the grid can place all of them.
 */

import type { TranslationKey } from "@/constants/i18n";
import { ROUTES, type RoutePath } from "@/constants/routes";

export interface NavPage {
  /** Stable key. For a Configuration section this is the rail's `section` value. */
  key: string;
  label: TranslationKey;
  /** The address, where the page has one of its own. */
  to?: RoutePath;
  /** Any one of these opens it; empty means everyone. */
  perms: readonly string[];
  /** The kind of record this page administers, where it owns one. */
  model?: string;
  /**
   * What the permission grid calls that record, when the page's own name is
   * too narrow for it — one setting can sit behind two sections.
   */
  gridLabel?: TranslationKey;
}

export interface NavArea {
  /** Stable key, used as the grid's section id. */
  key: string;
  label: TranslationKey;
  /**
   * Any one of these opens the area; empty means everyone.
   *
   * For a row that goes somewhere — `landing` is set — this is what that one
   * screen asks for, so the row is never offered to somebody the screen would
   * refuse. For a parent row that goes nowhere itself, it is the union of what
   * the pages beneath it ask for, so the row appears when any child would.
   */
  perms: readonly string[];
  /**
   * The page the sidebar row itself opens, where the row is a place rather
   * than a parent. Unset for the geography and maps groups, and for
   * Configuration, whose row opens a rail of eight sections.
   */
  landing?: string;
  /** Every kind of record administered here, page or no page. */
  models: readonly string[];
  pages: readonly NavPage[];
}

/**
 * The ten areas, in the order the sidebar reads them — nine of them rows, and
 * Roles, which is reached from the account menu at the foot of it instead.
 *
 * Member Analytics and Maps own no table, but they do own a codename —
 * `view__member_analytics` and `view__map`, declared as screens in the
 * backend's permission catalogue. Without one, showing somebody the analytics
 * meant granting `view__club`, which is a different thing entirely, and the two
 * could never be ticked apart. Dashboard is the one area still open to all: its
 * tiles close themselves one by one against what the person may read.
 */
export const NAV_AREAS: readonly NavArea[] = [
  {
    key: "dashboard",
    label: "nav.dashboard",
    landing: "dashboard",
    // Everyone reaches it; each tile is a door that opens only for its own
    // codename, and the summary endpoint returns a section per permission held.
    perms: [],
    models: [],
    pages: [{ key: "dashboard", label: "nav.dashboard", to: ROUTES.DASHBOARD, perms: [] }],
  },
  {
    key: "members",
    label: "nav.members",
    landing: "members.list",
    perms: ["view__member"],
    models: [
      "member",
      "member_note",
      "member_attachment",
      "member_log",
      "member_acquisition_detail",
      "member_amendment_detail",
      "member_card_history",
      "member_card_transaction",
      "member_print_history",
      "member_supplementary_history",
      "member_saved_filter",
    ],
    pages: [
      {
        key: "members.list",
        label: "nav.members",
        to: ROUTES.MEMBERS,
        perms: ["view__member"],
        model: "member",
      },
      {
        key: "members.new",
        label: "mem.addMember",
        to: ROUTES.MEMBER_NEW,
        // Any one of the four enrolments opens the address. Left at
        // `add__member` it asked for the ordinary tier alone, so somebody
        // given affiliates and nothing else was refused the page their own
        // grant is for. Which tier they may actually enrol is settled by the
        // page, from `?tier=`, since that is where the answer is known.
        perms: [
          "add__member",
          "add__member_affiliates",
          "add__member_employee",
          "add__member_complimentary",
        ],
      },
      {
        key: "members.detail",
        label: "page.memberDetail",
        to: ROUTES.MEMBER_DETAIL,
        perms: ["view__member"],
      },
      {
        key: "members.notes",
        label: "roles.model.memberNote",
        perms: ["view__member_note"],
        model: "member_note",
      },
      {
        key: "members.attachments",
        label: "roles.model.memberAttachment",
        perms: ["view__member_attachment"],
        model: "member_attachment",
      },
      {
        key: "members.log",
        label: "page.memberActivityLog",
        to: ROUTES.MEMBER_ACTIVITY_LOG,
        perms: ["view__member_log"],
        model: "member_log",
      },
    ],
  },
  {
    key: "member-analytics",
    label: "nav.memberAnalytics",
    landing: "member-analytics",
    // A screen, not a kind of record — the tallies it reads are rewritten whole
    // by a worker and belong to nobody. It still needs a codename of its own, or
    // showing somebody the analytics means handing them everything it reads.
    perms: ["view__member_analytics"],
    models: ["member_analytics"],
    pages: [
      {
        key: "member-analytics",
        label: "nav.memberAnalytics",
        to: ROUTES.MEMBER_ANALYTICS,
        perms: ["view__member_analytics"],
        model: "member_analytics",
      },
    ],
  },
  {
    key: "non-members",
    label: "nav.nonMembers",
    landing: "non-members",
    perms: ["view__non_member"],
    models: ["non_member"],
    pages: [
      {
        key: "non-members",
        label: "nav.nonMembers",
        to: ROUTES.NON_MEMBERS,
        perms: ["view__non_member"],
        model: "non_member",
      },
    ],
  },
  {
    key: "employees",
    label: "nav.employees",
    landing: "employees.list",
    perms: ["view__user"],
    models: ["user", "employee_activity_log"],
    pages: [
      {
        key: "employees.list",
        label: "nav.employees",
        to: ROUTES.EMPLOYEES,
        perms: ["view__user"],
        model: "user",
      },
      {
        key: "employees.log",
        label: "dash.employeeLog",
        to: ROUTES.EMPLOYEE_ACTIVITY_LOG,
        perms: ["view__employee_activity_log"],
        model: "employee_activity_log",
      },
    ],
  },
  {
    key: "helpdesk",
    label: "nav.helpdesk",
    landing: "helpdesk",
    // The queue is the area's one screen. The thread and the two note lists
    // are read on a ticket rather than anywhere of their own, so they own
    // codenames but no page — the grid still needs to place them, which is
    // what `models` is for.
    perms: ["view__helpdesk_ticket"],
    models: ["helpdesk_ticket", "helpdesk_comment", "helpdesk_note"],
    pages: [
      {
        key: "helpdesk",
        label: "nav.helpdesk",
        to: ROUTES.HELPDESK,
        perms: ["view__helpdesk_ticket"],
        model: "helpdesk_ticket",
      },
      {
        key: "helpdesk.ticket",
        label: "hd.ticket",
        to: ROUTES.HELPDESK_TICKET,
        perms: ["view__helpdesk_ticket"],
      },
      {
        key: "helpdesk.comments",
        label: "hd.tabComments",
        perms: ["view__helpdesk_comment"],
        model: "helpdesk_comment",
      },
      {
        key: "helpdesk.notes",
        label: "hd.notes",
        perms: ["view__helpdesk_note"],
        model: "helpdesk_note",
      },
    ],
  },
  {
    key: "location",
    label: "nav.locationConfig",
    perms: ["view__province", "view__city", "view__barangay"],
    models: ["province", "city", "barangay"],
    pages: [
      {
        key: "provinces",
        label: "nav.provinces",
        to: ROUTES.PROVINCES,
        perms: ["view__province"],
        model: "province",
      },
      {
        key: "cities",
        label: "nav.cities",
        to: ROUTES.CITIES,
        perms: ["view__city"],
        model: "city",
      },
      {
        key: "barangays",
        label: "nav.barangays",
        to: ROUTES.BARANGAYS,
        perms: ["view__barangay"],
        model: "barangay",
      },
    ],
  },
  {
    key: "maps",
    label: "nav.maps",
    // A parent row over two maps that show different things — where members
    // live, and where a club should go — so each has its own codename and the
    // row appears when either does. What a layer plots still asks for its own
    // permission on the server, so a map can be granted without the member list
    // coming with it.
    perms: ["view__member_population_map", "view__club_recommendation_map"],
    models: ["member_population_map", "club_recommendation_map"],
    pages: [
      {
        key: "member-population",
        label: "nav.memberPopulation",
        to: ROUTES.MEMBER_POPULATION,
        perms: ["view__member_population_map"],
        model: "member_population_map",
      },
      {
        key: "club-recommendation",
        label: "nav.clubRecommendation",
        to: ROUTES.CLUB_RECOMMENDATION,
        perms: ["view__club_recommendation_map"],
        model: "club_recommendation_map",
      },
    ],
  },
  {
    key: "yellow-page",
    label: "nav.yellowPage",
    landing: "yellow-page",
    // The row opens the listings. Categories are administered from inside the
    // same area but have no row of their own, so holding only their codename
    // is not a reason to offer a screen that would refuse you.
    perms: ["view__yellow_page"],
    models: ["yellow_page", "yellow_page_category"],
    pages: [
      {
        key: "yellow-page",
        label: "nav.yellowPage",
        to: ROUTES.YELLOW_PAGE,
        perms: ["view__yellow_page"],
        model: "yellow_page",
      },
      {
        key: "yellow-page-categories",
        label: "page.yellowPageCategories",
        to: ROUTES.YELLOW_PAGE_CATEGORIES,
        perms: ["view__yellow_page_category"],
        model: "yellow_page_category",
      },
    ],
  },
  {
    /**
     * Roles is the one area with no sidebar row.
     *
     * It sits in the account menu at the foot of the sidebar instead, beside
     * the profile and the password — the corner of the app for who you are
     * rather than what you are looking at. That is a decision about placement,
     * and this file is not where placement is decided: `app-shell.tsx` names
     * the areas it draws rows for, so an area it does not name simply has no
     * row. Everything else an area is for — what opens the address, what takes
     * it out of the interface, which section of the permission grid the records
     * belong to — is the same work whether the way in is a row or a menu item.
     *
     * It was the Configuration rail's ninth section until it moved out here,
     * which is why `view__group` is no longer among Configuration's `perms`:
     * holding it is a reason to reach Roles, and no longer a reason to be
     * offered a rail with nothing in it.
     */
    key: "roles",
    label: "nav.roleConfiguration",
    landing: "roles",
    perms: ["view__group"],
    models: ["group"],
    pages: [
      {
        key: "roles",
        label: "nav.roleConfiguration",
        to: ROUTES.ROLES,
        perms: ["view__group"],
        // The screen is Role Configuration; the records in it are roles. The
        // grid lists kinds of record, so it keeps the shorter word — the same
        // split Business Unit and Club already make.
        gridLabel: "cfg.roles",
        model: "group",
      },
    ],
  },
  {
    key: "configuration",
    label: "nav.configuration",
    // Eight sections behind one row: any one of them is reason enough to open
    // it, and the rail inside lists only the sections the same person may read.
    perms: [
      "view__club",
      "view__reason",
      "view__idle_time",
      "view__password_expiry",
      "view__notes_complimentary",
      "view__notes_endorsement",
      "view__promo_code",
      "view__business_type",
      "view__supplementary_limit",
    ],
    models: [
      "reason",
      "club",
      "promo_code",
      "business_type",
      "idle_time",
      "password_expiry",
      "notes_complimentary",
      "notes_endorsement",
      "supplementary_limit",
    ],
    // `key` is the rail's `?section=` value, so ConfigurationPage can look a
    // section up by the name already in the URL.
    pages: [
      {
        key: "notes",
        label: "cfg.notes",
        // The section is its tabs: it opens for anyone who may read one of them.
        perms: ["view__notes_complimentary", "view__notes_endorsement"],
      },
      // Each tab asks for itself. They used to ask for `view__club` or
      // `view__reason` — whichever they happened to list — so a tab could not be
      // shown or hidden without moving Business Unit or Reasons with it.
      // Pre-opening is the club list and nothing else, so it asks what a club
      // asks. A codename of its own would have been the same grant spelled
      // twice, and the two free to drift apart.
      {
        key: "notes.pre-opening",
        label: "cfg.preOpening",
        perms: ["view__club"],
      },
      {
        key: "notes.complimentary",
        label: "cfg.complimentary",
        perms: ["view__notes_complimentary"],
        model: "notes_complimentary",
        gridLabel: "roles.model.notesComplimentary",
      },
      {
        key: "notes.endorsement",
        label: "cfg.endorsement",
        perms: ["view__notes_endorsement"],
        model: "notes_endorsement",
        gridLabel: "roles.model.notesEndorsement",
      },
      { key: "reasons", label: "cfg.reasons", perms: ["view__reason"], model: "reason" },
      // Two sections, two permissions. They shared `access_policy_setting`, so
      // the idle timeout could not be given to anyone without the password
      // rules going with it; the API now scopes its fields the same way.
      {
        key: "idle-time",
        label: "cfg.idleTitle",
        perms: ["view__idle_time"],
        model: "idle_time",
      },
      {
        key: "password-expiry",
        label: "cfg.passwordExpiry",
        perms: ["view__password_expiry"],
        model: "password_expiry",
      },
      {
        key: "promo-codes",
        label: "cfg.promoCodes",
        perms: ["view__promo_code"],
        model: "promo_code",
      },
      {
        key: "business-type",
        label: "cfg.businessType",
        perms: ["view__business_type"],
        model: "business_type",
      },
      {
        key: "business-unit",
        label: "cfg.businessUnit",
        perms: ["view__club"],
        model: "club",
        // "Business Unit" is the rail section; the permission is the club's,
        // and it governs every place a club is listed — this section, Notes →
        // Pre-opening, and the "Requested by" list on Notes → Complimentary.
        // Naming the row after one of the three read as though it covered only
        // that one, which is how a second, redundant tab codename came to exist.
        gridLabel: "roles.model.club",
      },
      {
        key: "supplementary",
        label: "cfg.supplementary",
        perms: ["view__supplementary_limit"],
        model: "supplementary_limit",
      },
    ],
  },
];

/** Areas by key, for the callers that hold one. */
export const NAV_AREA = Object.fromEntries(
  NAV_AREAS.map((area) => [area.key, area]),
) as Readonly<Record<string, NavArea>>;

/** Every page in the map, flattened, in sidebar order. */
export const NAV_PAGES: readonly NavPage[] = NAV_AREAS.flatMap((area) => area.pages);

/** `model → area key`. The grid groups by this. */
export const AREA_OF_MODEL: ReadonlyMap<string, string> = new Map(
  NAV_AREAS.flatMap((area) => area.models.map((model) => [model, area.key] as const)),
);

/**
 * `model → the name of the page that administers it`.
 *
 * A row in the grid reads better as the screen it governs than as the table it
 * came from: `club` is "Business Unit" to everyone who uses this product, and
 * `group` is "Roles". Where no page claims a model the grid falls back to the
 * older label list and then to the API's own verbose name.
 */
export const PAGE_LABEL_OF_MODEL: ReadonlyMap<string, TranslationKey> = new Map(
  NAV_PAGES.flatMap((page) =>
    page.model ? [[page.model, page.gridLabel ?? page.label] as [string, TranslationKey]] : [],
  ),
);

/** The permissions that open one page, by key. Unknown key means unguarded. */
export const permsForPage = (key: string): readonly string[] =>
  NAV_PAGES.find((page) => page.key === key)?.perms ?? [];

/** The permissions that open one area, by key. Unknown key means unguarded. */
export const permsForArea = (key: string): readonly string[] => NAV_AREA[key]?.perms ?? [];

/**
 * Hiding, which is not the same question as reading.
 *
 * `perms` above answers "may this person see these records", and the API asks
 * it too. This answers "has somebody decided this screen does not belong in
 * their interface", and nothing but the browser asks it. The two came apart
 * because they are genuinely different: a role can need `view__club` so that
 * clubs fill a dropdown on some other page, and have no use for Business Unit
 * in the sidebar — before this, granting the first granted the second and
 * there was no way to say otherwise.
 *
 * Off by default. Nothing is hidden until somebody ticks it, so the question
 * an administrator answers is the rare one.
 *
 * It never widens anything: a screen is shown when the records may be read
 * *and* it is not hidden, so ticking nothing leaves permissions deciding
 * everything, exactly as before. And it is not enforced — `hide__club` guards
 * no route and never will. Hiding shuts a door, it does not lock one, and the
 * data behind it stays as reachable as `view__club` says. That is the feature,
 * not an oversight — see the backend's `catalog.HIDE_ACTION`.
 */
const HIDE = (model: string) => `hide__${model}`;

/**
 * A page somebody goes *to*: an address of its own — a sidebar row, or the
 * account menu's Roles — or a section of the Configuration rail. Those are the
 * things that can be taken out of the interface.
 *
 * A member's notes and a ticket's comments have models and pages here, but
 * they are parts of a screen rather than places of their own — there is no row
 * to take away, and `view__member_note` already decides whether the tab is
 * drawn. So: an address of its own, or a section of Configuration.
 */
const isDestination = (area: NavArea, page: NavPage) =>
  page.to !== undefined || area.key === "configuration";

/** `page key → the codename that takes it out of the interface`. */
const HIDE_OF_PAGE: ReadonlyMap<string, string> = new Map(
  NAV_AREAS.flatMap((area) =>
    area.pages.flatMap((page) =>
      page.model && isDestination(area, page) ? [[page.key, HIDE(page.model)] as const] : [],
    ),
  ),
);

/**
 * Every navigable model, sorted — the frontend's half of the agreement with
 * the backend's `NAVIGABLE`. `nav-browse.test.ts` reads both and fails if they
 * have drifted apart.
 */
export const NAVIGABLE_MODELS: readonly string[] = [
  ...new Set(
    NAV_AREAS.flatMap((area) =>
      area.pages.flatMap((page) =>
        page.model && isDestination(area, page) ? [page.model] : [],
      ),
    ),
  ),
].sort();

/** `page key → the area it belongs to`, for the fallback below. */
const AREA_OF_PAGE: ReadonlyMap<string, string> = new Map(
  NAV_AREAS.flatMap((area) => area.pages.map((page) => [page.key, area.key] as const)),
);

/**
 * What takes one page out of the interface. At most one codename, read the way
 * `perms` is read: holding it is the answer.
 *
 * A page that administers a record of its own answers for itself. A page that
 * does not — a member's detail, the enrolment form, a ticket — follows its
 * area's landing page, because it is a room inside a place rather than a
 * place: if Members is hidden, the way in to a member is hidden too, and a
 * typed address should not be the way around that.
 *
 * Empty means nothing can hide it.
 */
export const hidePermsForPage = (key: string): readonly string[] => {
  const own = HIDE_OF_PAGE.get(key);
  if (own) return [own];
  const area = AREA_OF_PAGE.get(key);
  const landing = area ? NAV_AREA[area]?.landing : undefined;
  const principal = landing ? HIDE_OF_PAGE.get(landing) : undefined;
  return principal ? [principal] : [];
};

/**
 * What takes one area's row out of the sidebar: its landing page being hidden.
 *
 * The landing page alone, not the union of the area's destinations. Members
 * has a list and an activity log, and hiding the log is not a reason to take
 * Members out of the sidebar. An area with no landing — the geography and maps
 * groups, and Configuration — is a parent over its children, and disappears
 * when they all do rather than on a rule of its own.
 */
export const hidePermsForArea = (key: string): readonly string[] => {
  const landing = NAV_AREA[key]?.landing;
  const codename = landing ? HIDE_OF_PAGE.get(landing) : undefined;
  return codename ? [codename] : [];
};

/**
 * The two questions about one screen, asked together.
 *
 * `allow` is `can` — may these records be read, the question the API asks too.
 * `hidden` is `hiddenFrom` — has this person been marked as hiding the screen,
 * which only the browser asks, and which a superuser is never subject to.
 *
 * They are combined here rather than at each caller because the answer is one
 * fact — "is this screen on this person's screen" — and it was drifting apart
 * in four places.
 */
export type Gate = (
  allow: (...codenames: string[]) => boolean,
  hidden: (...codenames: string[]) => boolean,
) => boolean;

/** Whether one page belongs on screen. */
export const pageGate =
  (key: string): Gate =>
  (allow, hidden) => {
    const perms = permsForPage(key);
    const marks = hidePermsForPage(key);
    return (perms.length === 0 || allow(...perms)) && !(marks.length > 0 && hidden(...marks));
  };

/**
 * Whether one area's row belongs in the sidebar.
 *
 * An area that goes somewhere — `landing` is set — is that page, and nothing
 * more: hiding the member activity log is not a reason to take Members out of
 * the menu, because the Members row opens the list.
 *
 * An area that goes nowhere itself is a parent over the pages beneath it, and
 * it is there while any one of them is. Configuration is the case that matters:
 * its row opens a rail of ten sections, so hiding one section leaves the row
 * alone and hiding all ten takes it away — there is nothing left behind it to
 * open. Without this it stayed in the sidebar and led to a rail with nothing
 * in it.
 */
export const areaGate =
  (key: string): Gate =>
  (allow, hidden) => {
    const area = NAV_AREA[key];
    if (!area) return true;
    if (area.landing) return pageGate(area.landing)(allow, hidden);
    const destinations = area.pages.filter((page) => HIDE_OF_PAGE.has(page.key));
    if (destinations.length === 0) return area.perms.length === 0 || allow(...area.perms);
    return destinations.some((page) => pageGate(page.key)(allow, hidden));
  };

/**
 * The gate for wherever an address leads.
 *
 * For a link that must not offer a door it cannot open. The activity logs are
 * the case: their back arrow goes up to Members or Employees, and a role can
 * hold `view__member_log` without holding `view__member` — reading the trail
 * of what happened is not reading the records it happened to. Offered anyway,
 * the arrow was a one-way trip to Unauthorized.
 *
 * An address the map does not know is left open: a link to somewhere that is
 * not a guarded screen is not something to second-guess.
 */
export const gateForPath = (path: string): Gate => {
  const page = NAV_PAGES.find((candidate) => candidate.to === path);
  return page ? pageGate(page.key) : () => true;
};

/**
 * The hiding half of a page's gate, without the reading half.
 *
 * For a door that is not about reading. Enrolling a member asks for `add__`,
 * not `view__member` — a clerk who may take somebody on but not browse the
 * list still has an Add Member to press — while a Members screen hidden from
 * their interface should take the tile with it. `pageGate` asks both
 * questions; this asks only the second, and the caller supplies the first.
 */
export const notHidden =
  (key: string): Gate =>
  (_allow, hidden) => {
    const marks = hidePermsForPage(key);
    return !(marks.length > 0 && hidden(...marks));
  };
